<template>
  <ExternalFilterResetWrapper :active="appliedSelection.length > 0"
                              :size="size"
                              @reset-filter="noFilterApply">
    <BDropdown ref="filter"
               :text="dropDownText"
               :size="size"
               class="multi-option-filter"
               @show="resetSelection">
      <BDropdownHeader>
        {{ tr('common.FILTER_BY') }}
      </BDropdownHeader>
      <TextFilter v-if="searchable"
                  ref="textFilter"
                  class="text-search mb-2"
                  @change="onFilterChanged"></TextFilter>
      <ul class="list-of-options">
        <li v-for="option in optionsFilteredBySearch"
            :key="option.value"
            :class="{ 'filter-active': isSelected(option.value) }"
            :title="option.text"
            class="dropdown-item filter-button clickable"
            @click="onSelect(option)">
          <span v-if="option.icon"
                :class="option.icon"></span>
          {{ option.text }}
        </li>
      </ul>
      <BDropdownItem v-if="optionsFilteredBySearch.length === 0"
                     disabled>
        {{ tr('common.SEARCH_QUERY_NO_RESULT_FOUND') }}
      </BDropdownItem>
      <ButtonElement :text="tr('common.APPLY')"
                     :variant="BootstrapVariant.SECONDARY"
                     :size="size"
                     class="apply mt-2"
                     block
                     icon-name="checkmark"
                     @click="onFilterApply();hide()">
      </ButtonElement>
    </BDropdown>
  </ExternalFilterResetWrapper>
</template>

<script>
  import { BDropdown, BDropdownHeader, BDropdownItem } from 'bootstrap-vue';
  import translator from '~/zf/frontend/common/mixins/translator';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import ButtonElement from '../../layout/ButtonElement.vue';
  import ExternalFilterResetWrapper from './ExternalFilterResetWrapper.vue';
  import TextFilter from './TextFilter.vue';

  export default {
    name: 'MultiOptionFilter',
    compatConfig: {
      MODE: 3,
      COMPONENT_FUNCTIONAL: true,
    },
    components: {
      TextFilter,
      ExternalFilterResetWrapper,
      ButtonElement,
      BDropdown,
      BDropdownHeader,
      BDropdownItem,
    },
    mixins: [translator],
    props: {
      defaultSelection: {
        type: Array,
        default: () => [],
      },
      filterOptions: Object,
      dropDownLabel: {
        type: String,
        required: true,
      },
      filterId: {
        type: String,
        required: true,
      },
      singleMode: {
        type: Boolean,
      },
      size: {
        type: String,
        default: BootstrapVariant.MEDIUM,
      },
      searchable: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['filter'],
    data() {
      return {
        BootstrapVariant,
        appliedSelection: [],
        selection: [],
        searchFilter: undefined,
        options: [],
      };
    },
    computed: {
      optionsFilteredBySearch() {
        if (this.searchFilter === undefined) {
          return this.options;
        }
        return this.options.filter((option) => option.text.toLowerCase().includes(this.searchFilter.toLowerCase()));
      },
      isNoOptionSelected() {
        return this.appliedSelection.length === 0;
      },
      isEveryOptionSelected() {
        return this.appliedSelection.length === this.options.length;
      },
      isFilterActive() {
        return this.appliedSelection.length > 0;
      },
      selectedItemsCount() {
        return this.appliedSelection.length;
      },
      statusText() {
        return this.tr(this.dropDownLabel);
      },
      dropDownText() {
        if (this.isNoOptionSelected || this.isEveryOptionSelected) {
          return this.statusText;
        }
        return `${this.statusText} (${this.selectedItemsCount})`;
      },
    },
    watch: {
      filterOptions: {
        handler(value) {
          this.options = Object.values(value).map((filterOption) => ({
            ...filterOption,
            text: this.tr(filterOption.label),
          }));
        },
        deep: true,
        immediate: true,
      },
    },
    mounted() {
      this.setToDefault();
    },
    methods: {
      isSelected(value) {
        return this.selection.includes(value);
      },
      onSelect(selection) {
        if (this.singleMode === false) {
          this.multiOptionSelect(selection);
        } else {
          this.singleOptionSelect(selection);
        }
      },
      multiOptionSelect(selection) {
        if (this.isSelected(selection.value) === true) {
          this.selection = this.selection.filter((value) => value !== selection.value);
        } else {
          this.selection = [...this.selection, selection.value];
        }
      },
      singleOptionSelect(selection) {
        if (this.isSelected(selection.value) === false) {
          this.selection = [selection.value];
        } else {
          this.selection = [];
        }
      },
      applySelection(selection) {
        this.selection = selection;
        this.onFilterApply();
      },
      noFilterApply() {
        this.applySelection([]);
      },
      onFilterApply() {
        this.appliedSelection = this.selection;
        if (this.isNoOptionSelected === true) {
          this.$emit('filter', this.filterId, undefined);
          return;
        }
        this.$emit('filter', this.filterId, this.appliedSelection);
      },
      setSelection(selection) {
        this.appliedSelection = selection;
        this.selection = selection;
      },
      setToDefault() {
        this.appliedSelection = this.defaultSelection;
        this.selection = this.defaultSelection;
      },
      resetSelection() {
        this.selection = this.appliedSelection;
      },
      hide() {
        this.$refs.filter.hide(true);
      },
      onFilterChanged(_, filter) {
        this.searchFilter = filter;
      },
    },
  };
</script>

<style scoped lang="scss">
    :deep(.dropdown-menu) {
      padding-bottom: 0;
      width: 250px;
    }

    :deep(.dropdown-item) {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .filter-button span {
      top: 1px;
      position: relative;
    }

    .clickable {
      cursor: pointer;
    }

    .apply {
      border-bottom: 0;
      border-right: 0;
      border-left: 0;
    }

    .dropdown-menu.show .list-of-options {
      max-height: 102px;
      overflow-y: auto;
      overflow-x: hidden;
      white-space: nowrap;
    }

    .text-search {
      height: 25px;
    }
</style>
